<template>
  <v-flex class="contact-info">
    <v-layout row wrap>
      <v-flex tag="h2">
        <v-layout justify-space-between row wrap>
          <v-flex class="pr-1">
            {{ memberProfileConstants.MY_CONTACT_INFORMATION }}
            <i v-if="isCurrentUser"
               class="fas fa-pencil-alt pl-1"
               data-qe-id="open_my-contact_dialog"
               @click="openMyContactEditDialog()"></i>
          </v-flex>
          <v-flex>
            <span v-if="isContactInfoAvailable" class="download-contact-info"
                  data-qe-id="mycontactInformation_downloadContactInfo" @click="downloadContactInfo(memberId)">
                {{ memberProfileConstants.CONTACT_INFORMATION.DOWNLOAD_CONTACT_INFO }}
                <i aria-hidden="true" class="fa fa-download ml-2"></i>
            </span>
          </v-flex>
        </v-layout>
      </v-flex>
      <v-flex v-if="hasErrorDownloadingInMobile(memberId)" class="mobile-download-error" xs12>
        <v-layout>
          <v-flex xs11>
            {{ memberDirectoryPageConstant.label.ERROR_DOWNLOADING_CONTACT }}
          </v-flex>
          <v-flex xs1>
            <i class="fas fa-times" @click="removeErrorDownload(memberId)"></i>
          </v-flex>
        </v-layout>
      </v-flex>
    </v-layout>
    <status-overlay :action="loadGetContactInformationAction" content-height="240px">
    </status-overlay>
    <v-flex v-if="!isContactInfoAvailable && isCurrentUser">
      {{ memberProfileConstants.DEFAULT_EDIT_TEXT.MY_CONTACT_INFORMATION }}
    </v-flex>
    <v-flex v-if="isContactInfoAvailable">
      <v-layout mb-0 row wrap>
        <v-flex v-if="checkEmailVisible(contactInformation.email)" class="contact-info__row" xs12>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.PRIMARY_EMAIL }}</v-flex>
          <a
              :href="`mailto:${contactInformation.email}`"
              class="email-link"
              data-qe-id="contactInfo_primaryEmail">{{ contactInformation.email }}</a>
        </v-flex>
        <v-flex v-if="checkEmailVisible(contactInformation.otherEmail)" class="contact-info__row" xs12>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.SECONDARY_EMAIL }}</v-flex>
          <a
              :href="`mailto:${contactInformation.otherEmail}`"
              class="email-link"
              data-qe-id="contactInfo_otherEmail">{{ contactInformation.otherEmail }}</a>
        </v-flex>
      </v-layout>
      <v-layout
          mb-0
          mt-0
          row
          wrap>
        <v-flex
            v-if="areMyPhoneNumbersDisplayOnMyProfile(isCurrentUser, contactInformation.businessPhoneOnProfile, contactInformation.businessPhone)"
            class="contact-info__row"
            xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.BUSINESS }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.countryCode,contactInformation.businessPhone)}`"
             data-qe-id="contactInfo_businessPhone">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.countryCode, contactInformation.businessPhone) }}
              {{ getExtension(contactInformation.businessPhoneExt) }}
            </v-flex>
          </a>
        </v-flex>
        <v-flex
            v-if="areMyPhoneNumbersDisplayOnMyProfile(isCurrentUser, contactInformation.mobilePhoneOnProfile, contactInformation.mobilePhone)"
            class="contact-info__row"
            xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.MOBILE }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.mobilePhoneCountryCode,contactInformation.mobilePhone)}`"
             data-qe-id="contactInfo_mobilePhone">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.mobilePhoneCountryCode, contactInformation.mobilePhone) }}
            </v-flex>
          </a>
        </v-flex>
        <v-flex
            v-if="areMyPhoneNumbersDisplayOnMyProfile(isCurrentUser, contactInformation.homePhoneOnProfile, contactInformation.homePhone)"
            class="contact-info__row"
            xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.HOME_PHONE }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.homePhoneCountryCode,contactInformation.homePhone)}`"
             data-qe-id="contactInfo_homePhone">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.homePhoneCountryCode, contactInformation.homePhone) }}
            </v-flex>
          </a>
        </v-flex>
        <v-flex
            v-if="areMyPhoneNumbersDisplayOnMyProfile(isCurrentUser, contactInformation.otherPhoneOnProfile, contactInformation.otherPhone)"
            class="contact-info__row"
            xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.OTHER_PHONE }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.otherPhoneCountryCode,contactInformation.otherPhone)}`"
             data-qe-id="contactInfo_otherPhone">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.otherPhoneCountryCode, contactInformation.otherPhone) }}
            </v-flex>
          </a>
        </v-flex>
        <v-flex v-if="contactInformation.whatsAppPhone" class="contact-info__row" xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.WHATSAPP }}</v-flex>
          <a slot="activator"
             :href="`https://wa.me/${formatPhoneForNumbersOnly(contactInformation.whatsAppCode,contactInformation.whatsAppPhone)}`"
             data-qe-id="contactInfo_whatsAppPhone"
             target="_blank">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.whatsAppCode, contactInformation.whatsAppPhone) }}
            </v-flex>
          </a>
        </v-flex>
        <v-flex v-if="contactInformation.fax" class="contact-info__row" xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.BUSINESS_FAX }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.faxCountryCode,contactInformation.fax)}`"
             data-qe-id="contactInfo_fax">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.faxCountryCode, contactInformation.fax) }}
            </v-flex>
          </a>
        </v-flex>
        <v-flex v-if="contactInformation.homeFax" class="contact-info__row" xs6>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.HOME_FAX }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.homeFaxCountryCode,contactInformation.homeFax)}`"
             data-qe-id="contactInfo_homeFax">
            <v-flex slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.homeFaxCountryCode, contactInformation.homeFax) }}
            </v-flex>
          </a>
        </v-flex>
      </v-layout>
      <v-layout v-for="address in addressList" :key="address.id" mb-0 mt-0 row wrap>
        <v-flex v-if="isCurrentUser || !address.isPrivate" class="contact-info__row" xs12>
          <v-flex tag="strong">{{ address.addressType }}</v-flex>
          <v-flex tag="span">{{ address.street }}</v-flex>
          <v-flex tag="span">{{ address.city }}, {{ address.state }} {{ address.postalCode }} | {{ address.country }}
          </v-flex>
        </v-flex>
      </v-layout>
      <v-layout mb-0 mt-0 row wrap>
        <v-flex v-if="contactInformation.asstFirstName" class="contact-info__row" xs12>
          <v-flex tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.EXECUTIVE_ASSISTANT }}</v-flex>
          <v-flex tag="small">{{ contactInformation.asstFirstName }} {{ contactInformation.asstLastName }}</v-flex>
          <a slot="activator"
             :href="`tel:${PhoneNumberFormat(contactInformation.asstPhoneCountryCode,contactInformation.assistantPhone)}`">
            <v-flex v-if="contactInformation.assistantPhone" slot="activator" tag="span">
              {{ PhoneNumberFormat(contactInformation.asstPhoneCountryCode, contactInformation.assistantPhone) }}
              {{ getExtension(contactInformation.assistantPhoneExtension) }}
            </v-flex>
          </a>
          <a
              :href="`mailto:${contactInformation.asstEmail}`"
              class="email-link"
              data-qe-id="contactInfo_asstEmail">{{ contactInformation.asstEmail }}</a>
          <v-flex v-if="contactInformation.asstEmail && showExecutiveAssistantCheckbox">
            <v-checkbox v-model="contactInformation.eaEventPlatformEmailOptIn"
                        :label=memberProfileConstants.CONTACT_INFORMATION.EVENT_PLATFORM_NOTIFICATIONS
                        class="mt-1 asst-email-checkbox"
                        color="primary"
                        disabled>{{ contactInformation.asstEmail }}
            </v-checkbox>
            <v-checkbox v-model="contactInformation.eaGlobalMktgEmailOptIn"
                        :label=memberProfileConstants.CONTACT_INFORMATION.GLOBAL_MARKETING_NOTIFICATIONS
                        class="mt-0 asst-email-checkbox"
                        color="primary"
                        disabled>
            </v-checkbox>
          </v-flex>
        </v-flex>
        <v-flex v-if="emergencyContactList && emergencyContactList.length>0" tag="strong" xs12>
          {{ memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_CONTACT }}
        </v-flex>
        <v-flex v-for="(item,index) in emergencyContactList" :key="`emergencyContact-${index}`" xs12>
          <v-flex v-if="item.Name" class="contact-info__row" xs8>
            <v-flex tag="small">{{ item.Name }}</v-flex>
            <a slot="activator" :href="`tel:${PhoneNumberFormat(item.PhoneCountryCode,item.Phone)}`">
              <v-flex slot="activator" tag="span">{{ PhoneNumberFormat(item.PhoneCountryCode, item.Phone) }}</v-flex>
            </a>
            <a
                :data-qe-id="'contactInfo_emergencyContactEmail'+index"
                :href="`mailto:${item.Email}`"
                class="email-link">{{ item.Email }}</a>
          </v-flex>
        </v-flex>
      </v-layout>
      <v-flex class="social">
        <v-btn
            v-if="contactInformation.linkedIn"
            :href="normalizeUrl(contactInformation.linkedIn)"
            color="ypo_blue_secondary"
            data-qe-id="contactInfo_linedinLink"
            fab
            small
            target="_blank">
          <img class="linkedin-image" src="../../assets/LinkedInIcon.png"/>
        </v-btn>
        <v-btn
            v-if="contactInformation.facebookURL"
            :href="normalizeUrl(contactInformation.facebookURL)"
            color="ypo_blue"
            data-qe-id="contactInfo_facebookURL"
            fab
            small
            target="_blank">
          <v-icon class="fab fa-facebook"></v-icon>
        </v-btn>
        <v-btn
            v-if="contactInformation.instagram"
            :href="normalizeUrl(contactInformation.instagram)"
            color="ypo_blue"
            data-qe-id="contactInfo_instragramLink"
            fab
            small
            target="_blank">
          <v-icon class="fab fa-instagram"></v-icon>
        </v-btn>
        <v-btn
            v-if="contactInformation.skype"
            :href="normalizeUrl(contactInformation.skype)"
            color="ypo_blue"
            data-qe-id="contactInfo_skypeURL"
            fab
            small
            target="_blank">
          <v-icon class="fab fa-skype"></v-icon>
        </v-btn>
        <v-btn
            v-if="contactInformation.twitterUrl"
            :href="normalizeUrl(contactInformation.twitterUrl)"
            color="ypo_blue"
            data-qe-id="contactInfo_twitterUrl"
            fab
            small
            target="_blank">
          <v-icon class="fa-brands fa-x-twitter"></v-icon>
        </v-btn>
        <v-btn
            v-if="contactInformation.whatsApp"
            :href="normalizeUrl(contactInformation.whatsApp)"
            color="ypo_blue"
            data-qe-id="contactInfo_whatsAppURL"
            fab
            small
            target="_blank">
          <v-icon class="fab fa-whatsapp"></v-icon>
        </v-btn>

      </v-flex>
    </v-flex>
    <v-dialog
        ref="myContactInformationEdit"
        v-model="isMycontactInformationDialog"
        content-class="medium"
        scrollable>
      <v-card class="myprofile-popup">
        <v-card-title primary-title>
          {{ memberProfileConstants.CONTACT_INFORMATION.TITLE }}
          <i class="fas fa-times" data-qe-id="close_dialog_btn" @click="isMycontactInformationDialog = false"></i>
        </v-card-title>
        <v-card-text v-if="isMycontactInformationDialog" class="inner-scrollbar">
          <v-flex v-if="!contactInformation" xs12>{{ memberProfileConstants.CONTACT_INFORMATION.SUB_TITLE }}</v-flex>
          <v-flex class="details-view email" xs12>
            <v-flex tag="h6">{{ memberProfileConstants.CONTACT_INFORMATION.EMAIL }} <i v-if="isCurrentUser"
                                                                                       class="fas fa-pencil-alt"
                                                                                       data-qe-id="email_edit_icon"
                                                                                       @click="openEmailEditDialog()"></i>
            </v-flex>
            <v-layout row wrap>
              <v-flex v-if="contactInformation.email" class="contact-info__row" xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.PRIMARY_EMAIL }}</v-flex>
                <a
                    :href="`mailto:${contactInformation.email}`"
                    class="email-link"
                    data-qe-id="contactInfo_primaryEmail">{{ contactInformation.email }}</a>
              </v-flex>
              <v-flex v-if="contactInformation.otherEmail" class="contact-info__row" xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.SECONDARY_EMAIL }}</v-flex>
                <a
                    :href="`mailto:${contactInformation.otherEmail}`"
                    class="email-link"
                    data-qe-id="contactInfo_otherEmail">{{ contactInformation.otherEmail }}</a>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="details-view" xs12>
            <v-flex tag="h6">{{ memberProfileConstants.CONTACT_INFORMATION.PHONE_NUMBER }} <i
                class="fas fa-pencil-alt" data-qe-id="open_phone_dialog" @click="openPhoneNumbersDailog()"></i></v-flex>
            <v-layout row wrap>
              <v-flex v-if="contactInformation.businessPhone" class="contact-info__row" sm4 xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.BUSINESS }}</v-flex>
                <v-flex tag="span">
                  {{ PhoneNumberFormat(contactInformation.countryCode, contactInformation.businessPhone) }}
                  {{ getExtension(contactInformation.businessPhoneExt) }}
                </v-flex>
              </v-flex>
              <v-flex v-if="contactInformation.mobilePhone" class="contact-info__row" sm4 xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.MOBILE }}</v-flex>
                <v-flex tag="span">
                  {{ PhoneNumberFormat(contactInformation.mobilePhoneCountryCode, contactInformation.mobilePhone) }}
                </v-flex>
              </v-flex>
              <v-flex v-if="contactInformation.otherPhone" class="contact-info__row" sm4 xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.OTHER_PHONE }}</v-flex>
                <v-flex tag="span">
                  {{ PhoneNumberFormat(contactInformation.otherPhoneCountryCode, contactInformation.otherPhone) }}
                </v-flex>
              </v-flex>
              <v-flex v-if="contactInformation.whatsAppPhone" class="contact-info__row" sm4 xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.WHATSAPP }}</v-flex>
                <v-flex tag="span">
                  {{ PhoneNumberFormat(contactInformation.whatsAppCode, contactInformation.whatsAppPhone) }}
                </v-flex>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex v-if="contactInformation.homePhone" class="contact-info__row" sm4 xs6>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.HOME_PHONE }}</v-flex>
                <v-flex tag="span">
                  {{ PhoneNumberFormat(contactInformation.homePhoneCountryCode, contactInformation.homePhone) }}
                </v-flex>
              </v-flex>
              <v-flex v-if="contactInformation.fax" class="contact-info__row" sm4 xs6>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.BUSINESS_FAX }}</v-flex>
                <v-flex tag="span">{{ PhoneNumberFormat(contactInformation.faxCountryCode, contactInformation.fax) }}
                </v-flex>
              </v-flex>
              <v-flex v-if="contactInformation.homeFax" class="contact-info__row" sm4 xs6>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.HOME_FAX }}</v-flex>
                <v-flex tag="span">
                  {{ PhoneNumberFormat(contactInformation.homeFaxCountryCode, contactInformation.homeFax) }}
                </v-flex>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="details-view" xs12>
            <v-flex tag="h6">{{ memberProfileConstants.CONTACT_INFORMATION.ADDRESS }}
              <span v-if="addressList.length< 2" class="add-edit-btn" data-qe-id="open_address_dialog"
                    @click="openAddressDialog()">
                                <i class="fas fa-pencil-alt"></i>
                                <i class="fas fa-plus-circle"></i>
                            </span>
              <v-flex v-if="addressList.length == 0" class="note" tag="span">
                {{ memberProfileConstants.CONTACT_INFORMATION.ADDRESS_HELP_TEXT }}
              </v-flex>
            </v-flex>
            <v-flex
                v-for="address in addressList"
                :key="address.id"
                class="contact-info__row"
                x12>
              <v-layout row wrap>
                <v-flex xs12>
                  <v-flex ml-0 tag="strong">{{ address.addressType }}
                    <i v-if="address.addressType && isCurrentUser" class="fas fa-pencil-alt"
                       data-qe-id="open_edit-address_dialog" @click="editAddressDialog(address)"></i>
                  </v-flex>
                  <v-flex tag="span">{{ address.street }}, {{ address.city }}, {{ address.state }}
                    {{ address.postalCode }} | {{ address.country }}
                  </v-flex>
                </v-flex>
              </v-layout>
            </v-flex>
          </v-flex>
          <v-flex class="details-view" xs12>
            <v-flex tag="h6">{{ memberProfileConstants.CONTACT_INFORMATION.EXECUTIVE_ASSISTANT }} <i
                class="fas fa-pencil-alt" data-qe-id="open_assistant_dialog"
                @click="openExecutiveAssistantEditDialog"></i></v-flex>
            <v-layout row wrap>
              <v-flex v-if="contactInformation.asstFirstName" class="contact-info__row" xs12>
                <v-flex ml-0 tag="strong">{{ memberProfileConstants.CONTACT_INFORMATION.EXECUTIVE_ASSISTANT }}</v-flex>
                <v-flex ml-0 tag="small">{{ contactInformation.asstFirstName }} {{ contactInformation.asstLastName }}
                </v-flex>
                <v-flex v-if="contactInformation.assistantPhone" tag="span">
                  {{ PhoneNumberFormat(contactInformation.asstPhoneCountryCode, contactInformation.assistantPhone) }}
                  {{ getExtension(contactInformation.assistantPhoneExtension) }}
                </v-flex>
                <a
                    :href="`mailto:${contactInformation.asstEmail}`"
                    class="email-link"
                    data-qe-id="contactInfo_asstEmail">{{ contactInformation.asstEmail }}</a>
                <v-flex v-if="contactInformation.asstEmail" class="executive-assist-notification">
                  <v-checkbox v-model="contactInformation.eaEventPlatformEmailOptIn"
                              :label=memberProfileConstants.CONTACT_INFORMATION.EVENT_PLATFORM_NOTIFICATIONS
                              class="mt-1 asst-email-checkbox"
                              color="primary"
                              disabled>
                  </v-checkbox>
                  <v-checkbox v-model="contactInformation.eaGlobalMktgEmailOptIn"
                              :label=memberProfileConstants.CONTACT_INFORMATION.GLOBAL_MARKETING_NOTIFICATIONS
                              class="mt-1 asst-email-checkbox"
                              color="primary"
                              disabled>
                  </v-checkbox>
                </v-flex>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="details-view" xs12>
            <v-flex tag="h6">{{ memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_CONTACT }}
              <span v-if="emergencyContactCount<3" class="add-edit-btn" data-qe-id="open_emergency_dialog"
                    @click="openEmergencyContactDialog()">
                                <i class="fas fa-pencil-alt"></i>
                                <i class="fas fa-plus-circle"></i>
                            </span>
            </v-flex>
            <v-layout row wrap>
              <v-flex v-for="(item,index) in emergencyContactList" :key="`emergencyContact-${index}`" xs12>
                <v-flex ml-0 tag="strong" xs12>{{ item.RelationshipToMember }}
                  <i v-if="item && isCurrentUser" class="fas fa-pencil-alt" data-qe-id="open_edit-emergency_dialog"
                     @click="editEmergencyContactDialog(item)"></i>
                </v-flex>
                <v-flex v-if="item.Name" class="contact-info__row" xs8>
                  <v-flex ml-0 tag="small">{{ item.Name }}</v-flex>
                  <v-flex tag="span">{{ PhoneNumberFormat(item.PhoneCountryCode, item.Phone) }}</v-flex>
                  <a
                      :data-qe-id="'contactInfo_emergencyContactEmail'+index"
                      :href="`mailto:${item.Email}`"
                      class="email-link">{{ item.Email }}</a>
                </v-flex>
              </v-flex>
            </v-layout>
          </v-flex>
          <v-flex class="details-view" xs12>
            <v-layout row wrap>
              <v-flex sm3 tag="h6" xs12>{{ memberProfileConstants.CONTACT_INFORMATION.SOCIAL_NETWORKS }}
                <i class="fas fa-pencil-alt" data-qe-id="close_dialog_btn" @click.stop="showSocialNetworkEdit=true"></i>
              </v-flex>
              <v-flex v-if="isCurrentUser" class="prefer-say-contactinfo">
                <v-checkbox v-model="contactInformation.doesNotParticipateInSocialMedia"
                            :disabled="isSocialMediaCountlessthan1()"
                            :label=memberProfileConstants.EDUCATION.PREFER
                            class="mt-0"
                            color="primary"
                            data-qe-id="prefer_not_say_socialnetworks"
                            @change="savePreferNotToSay()">
                </v-checkbox>
              </v-flex>
            </v-layout>
            <v-flex class="social">
              <v-btn
                  v-if="contactInformation.facebookURL"
                  :href="normalizeUrl(contactInformation.facebookURL)"
                  color="ypo_blue"
                  data-qe-id="contactInfo_facebookURL"
                  fab
                  small
                  target="_blank">
                <v-icon class="fab fa-facebook"></v-icon>
              </v-btn>
              <v-btn
                  v-if="contactInformation.instagram"
                  :href="normalizeUrl(contactInformation.instagram)"
                  color="ypo_blue"
                  data-qe-id="contactInfo_instragramLink"
                  fab
                  small
                  target="_blank">
                <v-icon class="fab fa-instagram"></v-icon>
              </v-btn>
              <v-btn
                  v-if="contactInformation.skype"
                  :href="normalizeUrl(contactInformation.skype)"
                  color="ypo_blue"
                  data-qe-id="contactInfo_skypeURL"
                  fab
                  small
                  target="_blank">
                <v-icon class="fab fa-skype"></v-icon>
              </v-btn>
              <v-btn
                  v-if="contactInformation.linkedIn"
                  :href="normalizeUrl(contactInformation.linkedIn)"
                  color="ypo_blue"
                  data-qe-id="contactInfo_linedinLink"
                  fab
                  small
                  target="_blank">
                <img class="linkedin-image" src="../../assets/LinkedInIcon.png"/>
              </v-btn>
              <v-btn
                  v-if="contactInformation.twitterUrl"
                  :href="normalizeUrl(contactInformation.twitterUrl)"
                  color="ypo_blue"
                  data-qe-id="contactInfo_twitterUrl"
                  fab
                  small
                  target="_blank">
                <v-icon class="fa-brands fa-x-twitter"></v-icon>
              </v-btn>
              <v-btn
                  v-if="contactInformation.whatsApp"
                  :href="normalizeUrl(contactInformation.whatsApp)"
                  color="ypo_blue"
                  data-qe-id="contactInfo_whatsAppURL"
                  fab
                  small
                  target="_blank">
                <v-icon class="fab fa-whatsapp"></v-icon>
              </v-btn>
            </v-flex>
          </v-flex>
        </v-card-text>
        <v-card-actions>
          <v-flex pa-3 text-xs-right>
            <v-btn class="primary_btn" data-qe-id="close_dialog_btn" flat @click="isMycontactInformationDialog = false">
              {{ appCommonConstants.CLOSE }}
            </v-btn>
          </v-flex>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-if="isEmailEdit" v-model="isEmailEdit" content-class="medium" scrollable>
      <v-card class="myprofile-popup edit-email-popup">
        <v-card-title>
          {{ memberProfileConstants.CONTACT_INFORMATION.EDIT_EMAIL }}
          <i class="fas fa-times" data-qe-id="close_dialog_btn" @click="closeEmailEditDailog()"></i>
        </v-card-title>
        <v-card-text class="inner-scrollbar">
          <v-form ref="EmailEditForm" v-model="editEmailValid" class="email-edit-form" lazy-validation>
            <v-layout row wrap>
              <v-flex xs8></v-flex>
              <v-flex class="form-title" text-xs-center xs2>
                {{ memberProfileConstants.CONTACT_INFORMATION.EMAIL_DISPLAY }}
              </v-flex>
              <v-flex class="form-title" text-xs-center xs2>
                {{ memberProfileConstants.CONTACT_INFORMATION.SEND_EMAIL }}
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex class="edit-email-popup__edit-email__form-item__label" xs2>
                <v-flex tag="span">{{ memberProfileConstants.CONTACT_INFORMATION.PRIMARY_EMAIL }}
                  {{ memberProfileConstants.COLON }}
                </v-flex>
                <v-flex tag="span">{{ memberProfileConstants.CONTACT_INFORMATION.SECONDARY_EMAIL }}
                  {{ memberProfileConstants.COLON }}
                </v-flex>
              </v-flex>
              <v-flex xs6>
                <v-text-field
                    ref="emailAddress"
                    v-model="editContactInformation.email"
                    :maxlength="memberProfileConstants.MAX_NAME_LENGTH"
                    :placeholder="memberProfileConstants.CONTACT_INFORMATION.PRIMARY_EMAIL"
                    :rules="memberProfileConstants.emailRules"
                    color="ypo_blue"
                    data-qe-id="contactInfo_primaryEmailEdit"
                    required
                    @blur="isEmailEmpty(editContactInformation.otherEmail, editContactInformation.email)"
                    @input="validateEmailInfo(editContactInformation.email, memberProfileConstants.CONTACT_INFORMATION.PRIMARY)"></v-text-field>
                <v-text-field
                    v-model="editContactInformation.otherEmail"
                    :maxlength="memberProfileConstants.MAX_NAME_LENGTH"
                    :placeholder="memberProfileConstants.CONTACT_INFORMATION.SECONDARY_EMAIL"
                    :rules="memberProfileConstants.emailRule"
                    color="ypo_blue"
                    data-qe-id="contactInfo_secondaryEmailEdit"
                    @blur="isEmailEmpty(editContactInformation.otherEmail, editContactInformation.email)"
                    @input="validateEmailInfo(editContactInformation.otherEmail, memberProfileConstants.CONTACT_INFORMATION.SECONDARY)">
                </v-text-field>
              </v-flex>
              <v-flex text-xs-center xs2>
                <v-radio-group v-model="radios" class="radio-item-group" text-xs-center>
                  <v-radio
                      :disabled="!primaryEmailValid"
                      :value="memberProfileConstants.CONTACT_INFORMATION.PRIMARY"
                      class="radio-item"
                      color="ypo_blue"
                      data-qe-id="EditEmail_priamaryEmailRadio"
                      v-on:change="isEmailAddressToDisplay(true, $event)"></v-radio>
                  <v-radio
                      :disabled="!secondaryEmailValid"
                      :value="memberProfileConstants.CONTACT_INFORMATION.SECONDARY"
                      class="radio-item"
                      color="ypo_blue"
                      data-qe-id="EditEmail_secondaryEmailRadio"
                      v-on:change="isEmailAddressToDisplay(true, $event)"></v-radio>
                </v-radio-group>
              </v-flex>
              <v-flex text-xs-center xs2>
                <v-checkbox v-model="isSendYpoEmailsSelected" :disabled="true" class="radio-item-group"></v-checkbox>
              </v-flex>
            </v-layout>
            <v-flex v-if="uniqueEmail" class="form-control">
                            <span class="required-field" data-qe-id="unique_email_contact_info">
                                {{ memberProfileConstants.EMAIL_FIELD_WARNING }}
                            </span>
            </v-flex>
          </v-form>
        </v-card-text>
        <status-bar :action="saveEmailDetailsAction"></status-bar>
        <v-card-actions>
          <v-flex pa-3 text-xs-right>
            <v-btn :disabled="!editEmailValid" class="primary_btn" data-qe-id="save_dialog_btn"
                   @click="saveEmailDetails()">
              {{ appCommonConstants.DONE }}
            </v-btn>
          </v-flex>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-show="isEmergencyContactEditDialog" v-model="isEmergencyContactEditDialog" content-class="medium"
              persistent
              scrollable>
      <v-card class="myprofile-popup">
        <v-card-title primary-title>
          {{ memberProfileConstants.CONTACT_INFORMATION.EDIT_MY_EMERGENCY_CONTACT }}
          <i class="fas fa-times" data-qe-id="close_dialog_btn" @click="closeEmergencyContactDialog()"></i>
        </v-card-title>
        <v-card-text class="inner-scrollbar">
          <v-flex v-for="(emergencyContact,index) in emergencyContacts" :key="`emergencyContact-${index}`">
            <v-form :id="index" :ref="`emergencyContact_${emergencyContact.order}`" v-model="emergencyContact.valid">
              <span class="required-label" mr-2>{{ memberProfileConstants.REQUIRED }}</span>
              <v-layout row wrap>
                <v-flex sm6 xs12>
                  <v-flex class="form-control">
                    <span
                        class="select-label">{{ memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_RELATION_TO_YOU }} {{
                        memberProfileConstants.ASTERISK
                      }}</span>
                    <v-autocomplete
                        v-model="emergencyContact.RelationshipToMember"
                        :items="emergencyContactRelationTypes"
                        :rules="memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_RELATIONSHIP_RULE"
                        attach
                        color="ypo_blue"
                        data-qe-id="relationship_select"
                        item-text="relationshipToMember"
                        required>
                    </v-autocomplete>
                  </v-flex>
                </v-flex>
                <v-flex sm6 xs12>
                  <v-flex class="form-control">
                    <span
                        class="select-label">{{ memberProfileConstants.NAME }} {{
                        memberProfileConstants.ASTERISK
                      }}</span>
                    <v-text-field
                        v-model="emergencyContact.Name"
                        :placeholder="memberProfileConstants.NAME"
                        :rules="memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_NAME_RULE"
                        color="ypo_blue"
                        data-qe-id="emergency-contact-name_input"
                        required>
                    </v-text-field>
                  </v-flex>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex sm6 xs12>
                  <v-flex class="form-control mb-4">
                    <span
                        class="select-label mb-2">{{
                        memberProfileConstants.CONTACT_INFORMATION.PHONE_NUMBER
                      }} {{ memberProfileConstants.ASTERISK }}</span>
                    <ypo-phone-number :class="!emergencyContact.isPhoneFieldValid ? 'error-color': ''"
                                      :country-code="emergencyContact.PhoneCountryCode"
                                      :phone="emergencyContact.Phone"
                                      dataqeid="emergency_contact_phone_number_input"
                                      @update="(data) => updateEmergencyContactPhone(data, emergencyContact)"
                    />
                    <v-flex v-show="!emergencyContact.isPhoneFieldValid" class="error-color" tag="small">
                      {{ emergencyContact.errorMessage }}
                    </v-flex>
                  </v-flex>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex sm6 xs12>
                  <v-flex class="form-control">
                    <span class="select-label">{{ memberProfileConstants.EMAIL_ADDRESS }}</span>
                    <v-text-field
                        v-model="emergencyContact.Email"
                        :placeholder="memberProfileConstants.EMAIL_ADDRESS"
                        :rules="memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_EMAIL_RULE"
                        color="ypo_blue"
                        data-qe-id="emergency-contact-email_input">
                    </v-text-field>
                  </v-flex>
                </v-flex>
                <v-flex sm6 xs12>
                  <v-flex class="form-control">
                    <span
                        class="select-label">{{
                        memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_RETYPE_EMAIL_ADDRESS
                      }}</span>
                    <v-text-field
                        v-model="emergencyContact.RetypeEmailAddress"
                        :error-messages="emailMatchError(emergencyContact)"
                        :placeholder="memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_RETYPE_EMAIL_ADDRESS"
                        color="ypo_blue"
                        data-qe-id="emergency-contact-email_retype_input"
                        @paste="$event.preventDefault()">
                    </v-text-field>
                  </v-flex>
                </v-flex>
              </v-layout>
              <v-layout row wrap>
                <v-flex class="btn-row" pl-0>
                  <v-btn
                      v-if="!emergencyContact.isEdit && emergencyContactCount<2"
                      :disabled="!isAllFormsValid"
                      class="primary_btn"
                      data-qe-id="addMoreEmergencyContactBtn"
                      @click="addEmergencyContactDialog()">
                    <i class="fas fa-plus-circle mr-2"></i> {{ memberProfileConstants.ADD_MORE }}
                  </v-btn>
                  <v-btn
                      v-if="emergencyContact.isEdit"
                      :data-qe-id="'deleteEmergencyContactBtn'+index"
                      :disabled="!emergencyContact.isDeletable"
                      class="primary_btn"
                      color="primary"
                      @click="deleteEmergenceyContactInfo(emergencyContact, index)">
                    <i class="fas fa-trash-alt mr-2"></i> {{ appCommonConstants.DELETE }}
                  </v-btn>
                  <v-flex v-if="emergencyContact.isSuccess" :data-qe-id="'saveSuccessLabel_'+index"
                          class="save-to-profile">
                    <i class="fas fa-check"></i> <span>{{ memberProfileConstants.SAVED_TO_PROFILE }}</span>
                  </v-flex>
                </v-flex>
              </v-layout>
              <v-dialog
                  v-model="deleteEmergencyContactDialog"
                  absolute
                  content-class="post-actions"
                  lazy>
                <v-card>
                  <v-flex tag="h6">{{ memberProfileConstants.DIALOG_CONFIRMATION_TITLE }}
                    <v-btn :data-qe-id="'deleteEmergencyContact_closeIcon'+index" icon
                           @click="deleteEmergencyContactDialog=false">
                      <v-icon size="20">close</v-icon>
                    </v-btn>
                  </v-flex>
                  <v-flex pb-3 pt-3>{{ memberProfileConstants.DIALOG_CONFIRMATION_DESC }}</v-flex>
                  <v-flex text-xs-right>
                    <status-bar :action="saveEmergencyContactAction"></status-bar>
                    <v-btn
                        :data-qe-id="'deleteEmergencyContact_closeBtn'+index"
                        class="primary_btn transparent"
                        flat
                        @click="deleteEmergencyContactDialog = false">
                      {{ memberProfileConstants.CANCEL }}
                    </v-btn>
                    <v-btn
                        :data-qe-id="'deleteEmergencyContact_continueBtn'+index"
                        class="primary_btn"
                        dark
                        @click="deleteEmergenceyContact()">
                      {{ memberProfileConstants.CONTINUE }}
                    </v-btn>
                  </v-flex>
                </v-card>
              </v-dialog>
            </v-form>
          </v-flex>
        </v-card-text>
        <status-bar v-if="!isDeleteInProgress" :action="saveEmergencyContactAction"></status-bar>
        <v-card-actions>
          <v-flex pa-3 text-xs-right>
            <v-btn class="primary_btn transparent" data-qe-id="cancel_dialog_btn"
                   @click="closeEmergencyContactDialog()">
              {{ appCommonConstants.CANCEL }}
            </v-btn>
            <v-btn
                :disabled="!isAllFormsValid"
                class="primary_btn"
                data-qe-id="save_dialog_btn"
                @click="saveEmergencyContactDetails()">
              {{ appCommonConstants.DONE }}
            </v-btn>
          </v-flex>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <social-network-edit v-if="showSocialNetworkEdit" :memberId="memberId" :visible="showSocialNetworkEdit"
                         @close="showSocialNetworkEdit=false"/>
    <v-dialog
        v-show="isExecutiveAssistantEditDialog"
        v-model="isExecutiveAssistantEditDialog"
        v-if="contactInformation"
        content-class="medium"
        lazy-validation
        persistent
        scrollable>
      <v-card class="myprofile-popup">
        <v-card-title primary-title>
          {{ memberProfileConstants.CONTACT_INFORMATION.EDIT_MY_EXECUTIVE_ASSISTANT }}
          <i class="fas fa-times" data-qe-id="close_dialog_btn" @click="closeExecutiveAssistantContactDialog()"></i>
        </v-card-title>
        <v-card-text class="inner-scrollbar">
          <v-form ref="executiveAssistantForm" v-model="isExecutiveEdit">
            <span class="required-label">{{ memberProfileConstants.REQUIRED }}</span>
            <v-layout row wrap>
              <v-flex sm6 xs12>
                <v-flex class="form-control">
                  <span
                      class="select-label">{{ memberProfileConstants.FIRST_NAME }} {{
                      memberProfileConstants.ASTERISK
                    }}</span>
                  <v-text-field
                      v-model="editExecutiveAssistant.asstFirstName"
                      :placeholder="memberProfileConstants.FIRST_NAME"
                      :rules="memberProfileConstants.CONTACT_INFORMATION.FIRST_NAME_RULE"
                      color="ypo_blue"
                      data-qe-id="assistant_first-name_input"
                      required>
                  </v-text-field>
                </v-flex>
              </v-flex>
              <v-flex sm6 xs12>
                <v-flex class="form-control">
                  <span
                      class="select-label">{{ memberProfileConstants.LAST_NAME }} {{
                      memberProfileConstants.ASTERISK
                    }}</span>
                  <v-text-field
                      v-model="editExecutiveAssistant.asstLastName"
                      :placeholder="memberProfileConstants.LAST_NAME"
                      :rules="memberProfileConstants.CONTACT_INFORMATION.LAST_NAME_RULE"
                      color="ypo_blue"
                      data-qe-id="assistant_last-name_input"
                      required>
                  </v-text-field>
                </v-flex>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex sm6 xs12>
                <v-flex class="form-control">
                  <span
                      class="select-label">{{
                      memberProfileConstants.CONTACT_INFORMATION.PHONE_NUMBER
                    }} {{ memberProfileConstants.ASTERISK }}</span>
                  <ypo-phone-number :country-code="contactInformation.asstPhoneCountryCode"
                                    :phone="contactInformation.assistantPhone"
                                    class="pt-2"
                                    dataqeid="assistant_phone_number_input"
                                    @update="updateExecutiveAssistantPhone"
                  />
                  <v-flex v-show="!editExecutiveAssistant.isPhoneFieldValid" class="error-color" tag="small">
                    {{ editExecutiveAssistant.errorMessage }}
                  </v-flex>
                </v-flex>
              </v-flex>
              <v-flex sm6 xs12>
                <v-flex class="form-control">
                  <span class="select-label">{{ memberProfileConstants.EXTENSION }}</span>
                  <v-text-field
                      v-model="editExecutiveAssistant.assistantPhoneExtension"
                      :maxlength="memberProfileConstants.MAX_PHONE_EXTENSION_LENGTH"
                      :placeholder="memberProfileConstants.EXTENSION"
                      color="ypo_blue"
                      data-qe-id="assistant_phone-extension_input"
                      @keypress="onlyNumber">
                  </v-text-field>
                </v-flex>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex sm6 xs12>
                <v-flex class="form-control">
                  <span class="select-label">{{ memberProfileConstants.EMAIL_ADDRESS }}</span>
                  <v-text-field
                      v-model="editExecutiveAssistant.asstEmail"
                      :placeholder="memberProfileConstants.EMAIL_ADDRESS"
                      :rules="memberProfileConstants.emailRule"
                      color="ypo_blue"
                      data-qe-id="assistant_email_input">
                  </v-text-field>
                </v-flex>
              </v-flex>
              <v-flex sm6 xs12>
                <v-checkbox v-model="editExecutiveAssistant.eaEventPlatformEmailOptIn"
                            :disabled="isEaEmailEmpty"
                            :label=memberProfileConstants.CONTACT_INFORMATION.EVENT_PLATFORM_NOTIFICATIONS
                            class="mt-1 asst-email-checkbox"
                            color="primary">
                </v-checkbox>
                <v-checkbox v-model="editExecutiveAssistant.eaGlobalMktgEmailOptIn"
                            :disabled="isEaEmailEmpty"
                            :label=memberProfileConstants.CONTACT_INFORMATION.GLOBAL_MARKETING_NOTIFICATIONS
                            class="mt-1 asst-email-checkbox"
                            color="primary">
                </v-checkbox>
              </v-flex>
            </v-layout>
            <v-layout row wrap>
              <v-flex class="btn-row" pl-0>
                <v-btn
                    v-if="isDeleteExectutiveDisplay"
                    class="primary_btn"
                    data-qe-id="deleteExecutiveContactBtn"
                    @click="deleteExecutiveAssistantContactDialog = true">
                  <i class="fas fa-trash-alt mr-2"></i> {{ appCommonConstants.DELETE }}
                </v-btn>
              </v-flex>
            </v-layout>
            <v-dialog
                v-model="deleteExecutiveAssistantContactDialog"
                absolute
                content-class="post-actions"
                lazy>
              <v-card>
                <v-flex tag="h6">{{ memberProfileConstants.DIALOG_CONFIRMATION_TITLE }}
                  <v-btn data-qe-id="deleteExecutiveAssistnat_closeIcon" icon
                         @click="deleteExecutiveAssistantContactDialog = false">
                    <v-icon size="20">close</v-icon>
                  </v-btn>
                </v-flex>
                <v-flex pb-3 pt-3>{{ memberProfileConstants.DIALOG_CONFIRMATION_DESC }}</v-flex>
                <v-flex text-xs-right>
                  <status-bar :action="saveExecutiveAssistantAction"></status-bar>
                  <v-btn
                      class="primary_btn transparent"
                      data-qe-id="deleteExecutiveAssistnat_closeBtn"
                      flat
                      @click="deleteExecutiveAssistantContactDialog = false">
                    {{ memberProfileConstants.CANCEL }}
                  </v-btn>
                  <v-btn
                      class="primary_btn"
                      dark
                      data-qe-id="deleteExecutiveAssistnat_continueBtn"
                      @click="deleteExecutiveAssistantInfo()">
                    {{ memberProfileConstants.CONTINUE }}
                  </v-btn>
                </v-flex>
              </v-card>
            </v-dialog>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-flex pa-3 text-xs-right>
            <status-bar v-if="!isDeleteInProgress" :action="saveExecutiveAssistantAction"></status-bar>
            <v-btn class="primary_btn transparent" data-qe-id="close_dialog_btn"
                   @click="closeExecutiveAssistantContactDialog()">
              {{ appCommonConstants.CANCEL }}
            </v-btn>
            <v-btn
                :disabled="!isExecutiveEdit || !editExecutiveAssistant.isPhoneFieldValid"
                class="primary_btn"
                data-qe-id="ExecutiveAssistnat_saveBtn"
                @click="saveExecutiveAssistantInfo()">
              {{ appCommonConstants.DONE }}
            </v-btn>
          </v-flex>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="phoneNumbersDialog" content-class="medium" scrollable width="900">
      <PhoneNumbersDialog
          v-if="phoneNumbersDialog"
          :modelPhoneNumber="modelPhoneNumber"
          :phoneNumbersDialog="phoneNumbersDialog"
          @click="handleDialog"/>
    </v-dialog>
    <address-edit-dialog
        v-if="isAddressEditDialog"
        v-model="isAddressEditDialog"
        :addressList="addressList"
        :addresses="addresses"
        :memberId="memberId">
    </address-edit-dialog>
  </v-flex>
</template>

<script>
import { memberProfileConstants } from "./memberProfile.constants.js";
import { mapActions, mapGetters, mapState } from "vuex";
import { storeAction } from "../../store/storeActions.constant";
import { storeGetters } from "../../store/storeGetters.constant";
import { exportContactCard } from "././../../common/utils/vCard.js";
import { appCommonConstants } from "../appCommon.constants.js";
import { isNullOrUndefined } from 'util';
import Vue from 'vue';
import { normalizeUrl, onlyNumber } from "../../common/utils/utils.js";
import SocialNetworkEdit from "./EditDialogues/SocialNetworkEdit";
import StatusBar from "../StatusBar/StatusBar";
import { eventBus } from '../../main';
import PhoneNumbersDialog from "./EditDialogues/PhoneNumbersDialog";
import StatusOverlay from '../StatusOverlay/StatusOverlay';
import downloadVcard from "../../mixins/downloadVcardMixin";
import { memberDirectoryPageConstant } from "@/components/MemberDirectoryPage/memberDirectoryPage.constants";
import AddressEditDialog from "./EditDialogues/AddressEditDialog";
import { gaDataLayerEvent } from "@/plugins/analytics";
import { gtmEventName } from "@/plugins/gtmAnalytics";
import YpoPhoneNumber from "@/components/YpoPhoneNumber/YpoPhoneNumber.vue";
import { formatPhoneNumberForDisplay } from "@/common/utils/phoneUtils";
import { routerNames } from "../appCommon.constants";

export default {
  name: "MyContactInformation",
  data: () => ({
    modelPhoneNumber: null,
    phoneNumbersDialog: false,
    memberProfileConstants,
    isMycontactInformationDialog: false,
    onlyNumber,
    exportContactCard,
    appCommonConstants,
    states: [],
    existingEmails: [],
    isExecutiveEdit: false,
    isEmailEdit: false,
    primaryEmailValid: false,
    secondaryEmailValid: false,
    editEmailValid: false,
    memberDirectoryPageConstant,
    editContactInformation: [],
    editExecutiveAssistant: {},
    globalDefault: 'primary',
    radios: null,
    isDeleteInProgress: false,
    isEmergencyContactEditDialog: false,
    isExecutiveAssistantEditDialog: false,
    deleteExecutiveAssistantContactDialog: false,
    deleteEmergencyContactDialog: false,
    emergencyContactsDialog: false,
    isEmergencyContactDialog: false,
    isDeleteExectutiveDisplay: false,
    isAllFormsValid: false,
    isAddressEditDialog: false,
    isAddressEdit: false,
    isSendYpoEmailsSelected: false,
    emergencyContacts: [],
    addresses: [],
    selectedAddress: null,
    isAllAddressFormsValid: false,
    deleteAddressDialog: false,
    showSocialNetworkEdit: false,
    normalizeUrl,
    address: {
      isPrimary: false,
      isPrivate: false,
      type: null,
      country: null,
      street: "",
      city: null,
      state: null,
      postalCode: "",
      valid: false,
      showStateField: false,
    },
    emergencyContact: {
      order: null,
      Name: null,
      RelationshipToMember: null,
      PhoneCountryCode: null,
      Phone: null,
      Email: "",
      RetypeEmailAddress: "",
    },
    selectedEmergencyContact: {
      index: null,
      record: null,
    },
    showExecutiveAssistantCheckbox: false,
    routerNames,
  }),
  components: {
    YpoPhoneNumber,
    AddressEditDialog,
    SocialNetworkEdit,
    StatusBar,
    PhoneNumbersDialog,
    StatusOverlay,
  },
  mixins: [
    downloadVcard,
  ],
  props: {
    memberId: {
      type: String,
      required: true,
    },
  },
  created() {
    this.getContactInformation(this.memberId);
    eventBus.$on('openEmailEditDialog', (() => {
      this.openEmailEditDialog();
    }));
    eventBus.$on('phoneNumbersDialog', (() => {
      this.openPhoneNumbersDailog();
    }));
    eventBus.$on('openAddressDialog', ((value) => {
      this.isMycontactInformationDialog = value;
    }));
    eventBus.$on('socialNetworkDialog', ((value) => {
      this.showSocialNetworkEdit = value;
    }));
    this.memberProfileConstants.emailRules.push(v => !this.existingEmails.includes(v) || this.memberProfileConstants.VALID_EMAIL_ADDRESS);
    const { mode, memberId } = this.$route.query;

    const isEditMode = mode === this.routerNames.EDIT || memberId === this.currentUserId;
    const isCurrentUserMatch = memberId === this.currentUserId;

    this.showExecutiveAssistantCheckbox = isEditMode && this.isCurrentUser && isCurrentUserMatch;

  },
  methods: {
    ...mapActions({
      getContactInformation: `memberProfile/${storeAction.getContactInformation}`,
      getVCardDetails: `memberProfile/${storeAction.getVCardDetails}`,
      saveEmailInformation: `memberProfile/${storeAction.saveEmailInformation}`,
      saveEmergencyContact: `memberProfile/${storeAction.saveEmergencyContact}`,
      saveAddress: `memberProfile/${storeAction.saveAddress}`,
      saveExecutiveAssistant: `memberProfile/${storeAction.saveExecutiveAssistant}`,
      updateSocialNetworkDetails: `memberProfile/${storeAction.updateSocialNetworkDetails}`,
    }),
    openMyContactEditDialog() {
      this.isMycontactInformationDialog = true;
    },
    getExtension(value) {
      if (value) {
        return `${this.memberProfileConstants.CONTACT_INFORMATION.EXT} ${value}`;
      }
      return "";
    },
    areMyPhoneNumbersDisplayOnMyProfile(isLoggedUser, isDisplayOnMyProfile, phNumber) {
      let returnValue = false;
      if (phNumber) {
        if (isLoggedUser) {
          returnValue = true;
        } else if (isDisplayOnMyProfile) {
          returnValue = true;
        }
      }
      return returnValue;
    },
    PhoneNumberFormat(countryCode, phoneNumber) {
      if (phoneNumber) {
        if (countryCode && phoneNumber.length >= memberProfileConstants.PHONE_NUMBER_LENGTH) {
          return formatPhoneNumberForDisplay(countryCode, phoneNumber);
        } else if (phoneNumber.length >= memberProfileConstants.PHONE_NUMBER_LENGTH) {
          return phoneNumber;
        }
      }
    },
    formatPhoneForNumbersOnly(areaCode, phoneNum) {
      let result = '';
      if (areaCode && phoneNum) {
        const combinedNumber = (`${areaCode}${phoneNum}`).split('');
        combinedNumber.forEach((item) => {
          // eslint-disable-next-line
          if (!isNaN(parseInt(item))) {
            result += item;
          }
        });
      }
      return result;
    },
    openPhoneNumbersDailog() {
      this.modelPhoneNumber = null;
      this.phoneNumbersDialog = true;
      this.modelPhoneNumber = Vue.util.extend({}, this.contactInformation);
      this.modelPhoneNumber.isDialog = true;
      this.modelPhoneNumber.currentUserID = this.memberId;

      if (!this.modelPhoneNumber.businessPhone) {
        this.modelPhoneNumber.disableForBusinessPhone = true;
      }

      if (!this.modelPhoneNumber.homePhone) {
        this.modelPhoneNumber.disableForHomePhone = true;
      }

      if (!this.modelPhoneNumber.mobilePhone) {
        this.modelPhoneNumber.disableForMobilePhone = true;
      }
      if (!this.modelPhoneNumber.otherPhone) {
        this.modelPhoneNumber.disableForOtherPhone = true;
      }
      if (!this.modelPhoneNumber.whatsAppPhone) {
        this.modelPhoneNumber.disableForWhatsApp = true;
      }
    },
    handleDialog(variable) {
      this.phoneNumbersDialog = variable;
    },
    openEmailEditDialog() {
      this.isEmailEdit = true;
      this.editContactInformation = Vue.util.extend({}, this.contactInformation);
      this.radios = this.fillRadioButton;
      this.isSendYpoEmailsSelected = !isNullOrUndefined(this.editContactInformation.email);
      this.validateEmailInfo(this.editContactInformation.email, this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY);
      this.validateEmailInfo(this.editContactInformation.otherEmail, this.memberProfileConstants.CONTACT_INFORMATION.SECONDARY);
      if (isNullOrUndefined(this.editContactInformation.emailAddresstoDisplay)) {
        this.editContactInformation.emailAddresstoDisplay = this.editContactInformation.email;
      }
    },
    isEmailAddressToDisplay(isSelected, value) {
      if (isSelected) {
        if (value === this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY) {
          this.editContactInformation.emailAddresstoDisplay = this.editContactInformation.email;
        } else {
          this.editContactInformation.emailAddresstoDisplay = this.editContactInformation.otherEmail;
        }
      }
    },
    isEmailEmpty(otherEmail, primaryEmail) {
      if (primaryEmail === '' && otherEmail !== '') {
        this.radios = this.memberProfileConstants.CONTACT_INFORMATION.SECONDARY;
        this.editContactInformation.emailAddresstoDisplay = this.editContactInformation.otherEmail;
      } else if (primaryEmail !== '' && otherEmail === '') {
        this.radios = this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY;
        this.editContactInformation.emailAddresstoDisplay = this.editContactInformation.email;
      }
    },
    saveEmailDetails() {
      const postBody = {
        id: this.memberId,
        email: this.editContactInformation.email,
        otherEmail: this.editContactInformation.otherEmail,
        emailAddresstoDisplay: this.radios === this.memberProfileConstants.CONTACT_INFORMATION.SECONDARY ?
            this.memberProfileConstants.CONTACT_INFORMATION.SECONDARY : this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY,
      };
      this.saveEmailInformation(postBody)
          .then((response) => {
            if (response.data.message && response.data.message.includes(this.memberProfileConstants.EMAIL_ALREADY_EXIST)) {
              this.existingEmails.push(this.editContactInformation.email);
              this.$refs.emailAddress.focus();
              this.$refs.EmailEditForm.validate();
            } else {
              this.closeEmailEditDailog();
            }
            this.trackContactInfoEdit(gtmEventName.C_Profile_Edit, `Email - ${this.memberId}${this.isRedirectFromConnectPlus ? ' | Connect+ Redirect' : ''}`);
          });
    },
    closeEmailEditDailog() {
      this.isEmailEdit = false;
      this.editContactInformation = [];
      this.radios = null;
      this.existingEmails = [];
      if (this.$refs.EmailEditForm && this.$refs.EmailEditForm.validate) {
        this.$refs.EmailEditForm.validate();
      }
    },
    validateEmailInfo(email, type) {
      if (email) {
        const regex = memberProfileConstants.EMAIL_REGEX;
        const matches = regex.test(email);
        if (matches) {
          if (type === this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY) {
            this.primaryEmailValid = true;
            this.isSendYpoEmailsSelected = true;
          } else {
            this.secondaryEmailValid = true;
          }
        } else if (type === this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY) {
          this.primaryEmailValid = false;
          this.isSendYpoEmailsSelected = false;
        } else {
          this.secondaryEmailValid = false;
        }
      } else if (type === this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY) {
        this.primaryEmailValid = false;
        this.isSendYpoEmailsSelected = false;
      } else {
        this.secondaryEmailValid = false;
      }
    },
    openExecutiveAssistantEditDialog() {
      this.editExecutiveAssistant = {};
      this.editExecutiveAssistant.isPhoneFieldValid = true;
      this.isExecutiveAssistantEditDialog = true;
      if (this.contactInformation.asstFirstName != null || this.contactInformation.asstLastName != null || this.contactInformation.asstEmail != null || this.contactInformation.asstPhoneCountryCode != null) {
        this.isDeleteExectutiveDisplay = true;
        this.editExecutiveAssistant = Vue.util.extend({}, this.contactInformation);
        this.editExecutiveAssistant.isPhoneFieldValid = !!this.contactInformation.assistantPhone;
      }
    },
    saveExecutiveAssistantInfo() {
      this.editExecutiveAssistant.Id = this.memberId;
      const data = { ...this.editExecutiveAssistant };
      this.saveExecutiveAssistant(data)
          .then(() => {
            this.closeExecutiveAssistantContactDialog();
            this.trackContactInfoEdit(gtmEventName.C_Profile_Edit, `Executive Assistant - ${this.memberId}${this.isRedirectFromConnectPlus ? ' | Connect+ Redirect' : ''}`);
          });
    },
    deleteExecutiveAssistantInfo() {
      const data = {};
      data.asstFirstName = null;
      data.asstLastName = null;
      data.asstEmail = null;
      data.assistantPhone = null;
      data.asstPhoneCountryCode = null;
      data.Id = this.memberId;
      this.isDeleteInProgress = true;
      this.saveExecutiveAssistant(data).then(() => {
        this.isDeleteInProgress = false;
        this.deleteExecutiveAssistantContactDialog = false;
        this.closeExecutiveAssistantContactDialog();
        this.editExecutiveAssistant = {};
        this.isDeleteExectutiveDisplay = false;
      });
    },
    closeExecutiveAssistantContactDialog() {
      this.isExecutiveAssistantEditDialog = false;
      this.editExecutiveAssistant = {};
      const executiveForm = this.$refs.executiveAssistantForm;
      if (executiveForm && executiveForm.resetValidation) {
        executiveForm.resetValidation();
      }
    },
    closeEmergencyContactDialog() {
      this.isEmergencyContactEditDialog = false;
      this.emergencyContacts = [];
    },
    openEmergencyContactDialog() {
      const record = Vue.util.extend({}, this.emergencyContact);
      record.isEdit = false;
      record.isSuccess = false;
      record.isPhoneFieldValid = true;
      record.order = this.getEmptyEmergencyCount();
      this.emergencyContacts = [];
      this.emergencyContacts.push(record);
      this.isEmergencyContactEditDialog = true;
    },
    getEmptyEmergencyCount() {
      let order = 0;
      if (this.emergencyContactList.filter(x => x.order === 1).length === 0) {
        order = 1;
      } else if (this.emergencyContactList.filter(x => x.order === 2).length === 0) {
        order = 2;
      } else if (this.emergencyContactList.filter(x => x.order === 3).length === 0) {
        order = 3;
      }
      return order;
    },
    addEmergencyContactDialog() {
      this.saveEmergencyContactDetails(true);
    },
    editEmergencyContactDialog(item) {
      const data = Vue.util.extend({}, item);
      data.isEdit = true;
      data.isDeletable = true;
      data.RetypeEmailAddress = data.Email;
      data.isPhoneFieldValid = true;
      this.emergencyContacts.push(data);
      this.isEmergencyContactEditDialog = true;
    },
    saveEmergencyContactDetails(isAddMore) {
      const data = Vue.util.extend({}, this.contactInformation);
      this.emergencyContacts.forEach((rec) => {
        data[`emergencyContactName${rec.order}`] = rec.Name;
        data[`emergencyContactEmail${rec.order}`] = rec.Email;
        data[`emergencyContactPhone${rec.order}`] = rec.Phone;
        data[`emergencyContactPhoneCountryCode${rec.order}`] = rec.PhoneCountryCode;
        data[`emergencyContactRelationship${rec.order}`] = rec.RelationshipToMember;
      });
      this.saveEmergencyContact(data).then(() => {
        if (isAddMore) {
          this.emergencyContacts.forEach((rec) => {
            rec.isDeletable = true;
            rec.isEdit = true;
            rec.isSuccess = true;
          });
          const record = Vue.util.extend({}, this.emergencyContact);
          record.isEdit = false;
          record.isDeletable = false;
          record.isPhoneFieldValid = false;
          record.order = this.getEmptyEmergencyCount();
          const index = this.emergencyContacts.length;
          setTimeout(() => {
            document.getElementById(index).scrollIntoView();
          }, 100);
          this.emergencyContacts.push(record);
        } else {
          this.closeEmergencyContactDialog();
        }
        this.trackContactInfoEdit(gtmEventName.C_Profile_Edit, `Emergency Contact - ${this.memberId}${this.isRedirectFromConnectPlus ? ' | Connect+ Redirect' : ''}`);
      });
    },
    deleteEmergenceyContactInfo(record, index) {
      this.deleteEmergencyContactDialog = true;
      this.selectedEmergencyContact.record = record;
      this.selectedEmergencyContact.index = index;
    },
    deleteEmergenceyContact() {
      const data = Vue.util.extend({}, this.contactInformation);
      const { record, index } = this.selectedEmergencyContact;
      data[`emergencyContactName${record.order}`] = null;
      data[`emergencyContactEmail${record.order}`] = null;
      data[`emergencyContactPhone${record.order}`] = null;
      data[`emergencyContactPhoneCountryCode${record.order}`] = null;
      data[`emergencyContactRelationship${record.order}`] = null;
      this.isDeleteInProgress = true;
      this.saveEmergencyContact(data).then(() => {
        this.deleteEmergencyContactDialog = false;
        this.isDeleteInProgress = false;
        if (this.emergencyContacts.length === 1) {
          this.closeEmergencyContactDialog();
        } else {
          Vue.delete(this.emergencyContacts, index);
        }
        this.emergencyContacts.forEach((form) => {
          const [ref] = this.$refs[`emergencyContact_${form.order}`];
          if (ref && ref.resetValidation) {
            ref.resetValidation();
          }
        });
      });
    },
    editAddressDialog(item) {
      const data = Vue.util.extend({}, item);
      data.isEdit = true;
      data.isDeletable = true;
      data.addressTypes = Vue.util.extend([], this.addressTypes);
      if (this.addressList && this.addressList.length > 1) {
        const type = data.order === 1 ? this.contactInformation.address2Type : this.contactInformation.address1Type;
        data.addressTypes.splice(this.addressTypes.findIndex(x => x.addressType === type), 1);
      }
      this.addresses = [];
      this.addresses.push(data);
      this.isAddressEditDialog = true;
      this.isAddressEdit = true;
    },
    emailMatchError(data) {
      return ((data.Email ? data.Email.toLowerCase() : "") === (data.RetypeEmailAddress ? data.RetypeEmailAddress.toLowerCase() : "")) ? "" :
          this.memberProfileConstants.CONTACT_INFORMATION.EMERGENCY_EMAIL_MATCH_RULE;
    },
    openAddressDialog() {
      this.isAddressEdit = false;
      this.isAddressEditDialog = true;
      const record = Vue.util.extend({}, this.address);
      this.addresses = [];
      record.isEdit = false;
      record.order = this.addressList.find(x => x.order === 1) ? 2 : 1;
      record.addressTypes = Vue.util.extend([], this.addressTypes);
      if (this.addressList && this.addressList.length > 0) {
        const type = record.order === 1 ? this.contactInformation.address2Type : this.contactInformation.address1Type;
        record.addressTypes.splice(this.addressTypes.findIndex(x => x.addressType === type), 1);
      }
      this.addresses.push(record);
    },
    checkEmailVisible(email) {
      if (this.contactInformation && this.contactInformation !== "" && !isNullOrUndefined(email) && email !== "") {
        if (email === this.contactInformation.emailAddresstoDisplay || this.isCurrentUser) {
          return true;
        }
      }
      return false;
    },
    isSocialMediaCountlessthan1() {
      if (this.contactInformation.facebookMessenger ||
          this.contactInformation.facebookURL ||
          this.contactInformation.instagram ||
          this.contactInformation.linkedIn ||
          this.contactInformation.qq ||
          this.contactInformation.skype ||
          this.contactInformation.twitterUrl ||
          this.contactInformation.vk ||
          this.contactInformation.whatsApp ||
          this.contactInformation.youTube) {
        return true;
      }
      return false;
    },
    savePreferNotToSay() {
      const parms = {
        personId: this.memberId,
        doesNotParticipateInSocialMedia: this.contactInformation.doesNotParticipateInSocialMedia,
        socialNetworkInfo: {},
      };
      this.updateSocialNetworkDetails({ ...parms });
    },
    updateEmergencyContactPhone(data, emergencyContact) {
      emergencyContact.Phone = data.phone;
      emergencyContact.PhoneCountryCode = data.countryCode;
      emergencyContact.isPhoneFieldValid = !data.isInvalid && data.phone.length;
      emergencyContact.errorMessage = data.phone.length
          ? this.memberProfileConstants.PHONE_NUMBER_ERROR
          : this.memberProfileConstants.PHONE_NUMBER_REQUIRED;
    },
    updateExecutiveAssistantPhone(data) {
      this.editExecutiveAssistant.assistantPhone = data.phone;
      this.editExecutiveAssistant.asstPhoneCountryCode = data.countryCode;
      this.editExecutiveAssistant.isPhoneFieldValid = !data.isInvalid && data.phone.length;
      this.editExecutiveAssistant.errorMessage = data.phone.length
          ? this.memberProfileConstants.PHONE_NUMBER_ERROR
          : this.memberProfileConstants.PHONE_NUMBER_REQUIRED;
    },
    trackContactInfoEdit(trackingKey, trackingData) {
      gaDataLayerEvent(trackingKey, trackingData);
    },
  },
  computed: {
    ...mapGetters({
      contactInformation: `memberProfile/${storeGetters.contactInformation}`,
      currentUserId: `memberProfile/${storeGetters.currentUserId}`,
      vCardDetails: `memberProfile/${storeGetters.vCardDetails}`,
      userProfile: `memberProfile/${storeGetters.userProfile}`,
      emergencyContactRelationTypes: `memberProfile/${storeGetters.emergencyContactRelationTypes}`,
      addressTypes: `memberProfile/${storeGetters.addressTypes}`,
      countryAndStates: `memberProfile/${storeGetters.countryAndStates}`,
      chapterAdminEditable: `memberProfile/${storeGetters.chapterAdmin}`,
    }),
    ...mapState("actionState", ["actions"]),
    uniqueEmail() {
      return this.existingEmails.includes(this.editContactInformation.email);
    },
    isEaEmailEmpty() {
      return !this.editExecutiveAssistant.asstEmail || this.editExecutiveAssistant.asstEmail.trim() === '';
    },
    isRedirectFromConnectPlus() {
      return this.$route && this.$route.query
          && this.$route.query.connectPlusRedirect && this.$route.query.connectPlusRedirect === 'true';
    },
    isCurrentUser() {
      return this.memberId === this.currentUserId || this.chapterAdminEditable;
    },
    addressList() {
      const addresses = [];
      const contactInfo = this.contactInformation;
      if (contactInfo !== "" && (contactInfo.mailingCity != null || contactInfo.mailingStreet != null || contactInfo.mailingState !== null)) {
        addresses.push({
          order: 1,
          isPrimary: contactInfo.isAddress1Primary,
          isPrivate: contactInfo.isAddress1Private,
          type: contactInfo.address1Type,
          addressType: contactInfo.address1Type !== null && contactInfo.address1Type !== "" ? `${contactInfo.address1Type} Address` : memberProfileConstants.CONTACT_INFORMATION.BUSINESS_ADDRESS,
          street: contactInfo.mailingStreet,
          city: contactInfo.mailingCity,
          state: contactInfo.mailingState,
          postalCode: contactInfo.mailingPostalCode,
          country: contactInfo.mailingCountry,
          showStateField: false,
        });
      }
      if (contactInfo !== "" && (contactInfo.otherStreet != null || contactInfo.otherCity != null || contactInfo.otherState !== null)) {
        addresses.push({
          order: 2,
          isPrimary: contactInfo.isAddress2Primary,
          isPrivate: contactInfo.isAddress2Private,
          type: contactInfo.address2Type,
          addressType: contactInfo.address2Type !== null && contactInfo.address2Type !== "" ? `${contactInfo.address2Type} Address` : memberProfileConstants.CONTACT_INFORMATION.HOME_ADDRESS,
          street: contactInfo.otherStreet,
          city: contactInfo.otherCity,
          state: contactInfo.otherState,
          postalCode: contactInfo.otherPostalCode,
          country: contactInfo.otherCountry,
          showStateField: false,
        });
      }
      addresses.sort((a, b) => {
        const data1 = a.isPrimary;
        const data2 = b.isPrimary;
        if (data1 < data2) {
          return 1;
        }
        if (data1 > data2) {
          return -1;
        }
        return 0;
      });
      return addresses;
    },
    fillRadioButton() {
      if (!isNullOrUndefined(this.contactInformation.otherEmail) && this.contactInformation.otherEmail === this.contactInformation.emailAddresstoDisplay) {
        return this.memberProfileConstants.CONTACT_INFORMATION.SECONDARY;
      }
      return this.memberProfileConstants.CONTACT_INFORMATION.PRIMARY;
    },
    isContactInfoAvailable() {
      return this.contactInformation && this.contactInformation.email !== null;
    },
    emergencyContactCount() {
      return this.emergencyContactList.length;
    },
    emergencyContactList() {
      const emergencyContacts = [];
      const contactInfo = this.contactInformation;
      for (let i = 1; i <= 3; i += 1) {
        if (contactInfo[`emergencyContactName${i}`] != null) {
          emergencyContacts.push({
            order: i,
            Name: contactInfo[`emergencyContactName${i}`],
            Email: contactInfo[`emergencyContactEmail${i}`],
            Phone: contactInfo[`emergencyContactPhone${i}`],
            PhoneCountryCode: contactInfo[`emergencyContactPhoneCountryCode${i}`],
            RelationshipToMember: contactInfo[`emergencyContactRelationship${i}`],
          });
        }
      }
      emergencyContacts.sort((a, b) => {
        const nameA = a.Name.toLowerCase();
        const nameB = b.Name.toLowerCase();
        if (nameA < nameB) {
          return -1;
        }
        if (nameA > nameB) {
          return 1;
        }
        return 0;
      });
      return emergencyContacts;
    },
    AllFormsValid() {
      return this.emergencyContacts.map(x => x.valid && x.isPhoneFieldValid);
    },
    saveEmergencyContactAction() {
      return this.actions[storeAction.saveEmergencyContact];
    },
    saveEmailDetailsAction() {
      return this.actions[storeAction.saveEmailInformation];
    },
    saveExecutiveAssistantAction() {
      return this.actions[storeAction.saveExecutiveAssistant];
    },
    loadGetContactInformationAction() {
      return this.actions[storeAction.getContactInformation];
    },
  },
  watch: {
    AllFormsValid: {
      handler(newval, oldval) {
        if (newval !== oldval) {
          this.isAllFormsValid = this.AllFormsValid.filter(x => x === false).length === 0;
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../common/styles/_colors.scss";
@import "../../common/styles/_responsive.scss";

.contact-info {
  border-bottom: 1px solid $ypo-open-blue-gray;
  margin-bottom: 20px;
  padding: 0 20px 10px 20px;
  position: relative;

  .download-contact-info {
    color: $ypo-dark-gray;
    font-size: 12px;
    cursor: pointer;

    &:hover {
      color: $ypo-cta-hover;
    }
  }

  h2 {
    color: $ypo_blue;
    font-family: "Raleway", sans-serif;
    font-size: 22px;
    font-weight: 500;
    margin-bottom: 10px;
    line-height: 26px;
    position: relative;

    span {
      color: $ypo-cta;
      font-size: 14px;
      font-weight: bold;
      line-height: 19px;
      cursor: pointer;
    }

    i {
      color: $ypo-dark-background-gray;
      font-size: 14px;
      position: relative;
      margin-right: 0px;
      cursor: pointer;
    }

    em {
      color: $ypo-cta;
      font-family: "Noto Sans";
      font-size: 14px;
      line-height: 24px;
      font-style: normal;
      margin-left: 10px;
      cursor: pointer;
    }
  }

  .layout {
    margin: 15px 0;
  }

  &__row {
    margin-bottom: 15px;

    strong {
      color: $ypo-gray-text;
      font-size: 14px;
      font-weight: bold;
      line-height: 24px;
      display: block;
    }

    span {
      color: $ypo-cta;
      font-size: 14px;
      line-height: 24px;
      display: block;
    }

    small {
      display: block;
      color: $ypo-gray-text;
      font-size: 14px;
      line-height: 24px;
    }

    .email-link {
      text-transform: initial;
      text-decoration: none;
      color: $ypo-cta;
      font-size: 14px;
      line-height: 24px;
      margin-top: -5px;
      letter-spacing: 0.29px;
      overflow: hidden;
      white-space: nowrap;
      display: block;
      text-overflow: ellipsis;
      padding: 5px 10px 0 0;
    }
  }

  .social {
    text-align: left;

    .v-btn {
      box-shadow: none;
      margin: 0 15px 20px 15px;
      color: $white !important;

      &:hover {
        opacity: 0.8;
      }

      i {
        font-size: 18px;
        line-height: normal;
        display: inherit;
      }
    }
  }

  @media (max-width: 1199px) {
    padding: 0 0 20px 0;
  }

  .overlay {
    top: 35px;
  }
}

.email {
  border-top: none !important;
}

.v-dialog {
  .contact-info {
    &__row {

      strong {
        display: block;
        margin-left: 0px;
      }

      span {
        display: block;
        color: $ypo-gray-text;
      }

      .email-link {
        text-transform: initial;
        text-decoration: none;
        color: $ypo-gray-text;
        font-size: 14px;
        line-height: 24px;
        margin-top: -5px;
        letter-spacing: 0.29px;
        padding-left: 0px;
      }
    }
  }

  .social {
    text-align: left;
    margin-top: 20px;

    .v-btn {
      box-shadow: none;
      margin: 0 15px 20px 15px;
      color: $white;

      i {
        font-size: 18px;
        line-height: normal;
        display: inherit;
        margin-left: 3px !important;
      }
    }
  }
}

@include breakpoint(xs) {
  .email-edit-form {
    max-width: 800px;
    width: 800px;
  }
}

.linkedin-image {
  height: 40px;
}
</style>
<style lang="scss">
@import "../../common/styles/_colors.scss";
@import "../../common/styles/_responsive.scss";

.edit-email-popup {
  &__edit-email {
    &__form-item {
      &__label {
        span {
          display: block;
          margin: 14px 0 29px 0;
          color: $ypo_blue;
          font-weight: 600;
          font-size: 13px;
        }
      }
    }
  }

  .v-text-field {
    margin-top: 0;
    padding-top: 0;

  }

  .radio-item {
    margin: 8px 0 20px 0;
  }

  .radio-item-group {
    margin-top: 14px;
    display: inline-block;
    margin-left: 18px;
  }

  .form-title {
    color: $ypo_blue;
    font-weight: 600;
    font-size: 13px;
  }
}

.contact-info {
  &__row {
    a {
      text-decoration: none;
    }
  }
}

.prefer-say-contactinfo {
  i {
    font-size: 22px !important;
  }

  @include breakpoint(xs) {
    margin-top: 5px;
    margin-left: -8px;
  }
}

.executive-assist-notification {
  i {
    font-size: 22px !important;
  }

  @include breakpoint(xs) {
    margin-top: 5px;
    margin-left: -8px;
  }
}

.mobile-download-error {
  font-size: 12px;
  color: $ypo-error-red;
}

.form-control {
  .vue-tel-input {
    box-shadow: none !important;
    border: 0;
    border-radius: 0;
    border-bottom: 1px solid rgba($black, 0.5);
    margin-top: 13px;

    .dropdown {
      .country-code {
        font-size: 14px;
        font-weight: 600;
      }
    }

    &:focus-within {
      border-bottom: 1.5px solid;
    }

    .error-color {
      .vue-tel-input {
        border-bottom: 1px solid $ypo-error-red;
      }
    }
  }
}

.asst-email-checkbox.v-input--selection-controls {
  .v-input__slot {
    label {
      font-family: Raleway, serif;
      font-weight: 500;
      color: $ypo-dark-gray !important;
      font-size: 14px !important;
    }
  }

  .v-messages {
    display: none !important;
  }
}

</style>
